import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { registerBlock } from '../../blockRegistry';
import {
  MousePointer2, Type, Square, Circle, Minus, ArrowUpRight, Pencil, Mountain, Sparkles, Undo2, Redo2, Layers, BoxSelect, Lock, Expand, ImagePlus, Image as ImageIcon, Loader2, Wand2,
} from 'lucide-react';
import { AiWhiteboardDialog } from './AiWhiteboardDialog';
import { PictureToPerfectDialog } from './PictureToPerfectDialog';
import { WB_W, WB_H, uid, moveObj, WhiteboardShapes } from './whiteboardShapes';
import { WhiteboardView } from './WhiteboardView';
import { WhiteboardFullscreen } from './WhiteboardFullscreen';
import { icebergStarter } from './starter';
import { Btn, Field, ArrangeBar } from './wbControls';
import { LayersPanel } from './LayersPanel';
import { useWhiteboardHistory } from './useWhiteboardHistory';
import { SelectionLayer } from './wbSelection';
import { resizeToPoint, rotateObj, angleAt, centerOf } from './wbGeometry';
import { GeometryPanel } from './wbGeometryPanel';
import { uploadImageLayer } from './imageLayer';
import {
  clickSelection, toggleUnit, moveSelection, groupSelection, ungroupSelection, patchMany, duplicateSelection,
  unionBox, groupMembers, groupName,
} from './whiteboardOps';

export const DEFAULT_WHITEBOARD = { title: '', background: '#FFFFFF', objects: [] };

const TOOLS = [
  { id: 'select', label: 'Select', Icon: MousePointer2 },
  { id: 'text', label: 'Text', Icon: Type },
  { id: 'rect', label: 'Rectangle', Icon: Square },
  { id: 'ellipse', label: 'Circle', Icon: Circle },
  { id: 'line', label: 'Line', Icon: Minus },
  { id: 'arrow', label: 'Arrow', Icon: ArrowUpRight },
  { id: 'pen', label: 'Draw', Icon: Pencil },
];

const SHAPE_DEFAULTS = {
  rect: { fill: '#E2E8F0', stroke: '#334155', strokeWidth: 2, opacity: 1, radius: 8 },
  ellipse: { fill: '#E2E8F0', stroke: '#334155', strokeWidth: 2, opacity: 1 },
  line: { stroke: '#0F172A', strokeWidth: 3, opacity: 1 },
  arrow: { stroke: '#0F172A', strokeWidth: 3, opacity: 1 },
  pen: { stroke: '#0F172A', strokeWidth: 3, opacity: 1 },
  text: { text: 'Text', fontSize: 28, color: '#0F172A', opacity: 1 },
};

const clampPt = (p) => ({ x: Math.max(0, Math.min(WB_W, p.x)), y: Math.max(0, Math.min(WB_H, p.y)) });

function applyDraft(objects, d) {
  if (!d) return objects;
  if (d.kind === 'create') return [...objects, d.obj];
  if (d.kind === 'move' || d.kind === 'rotate') return objects.map((o) => d.live[o.id] || o);
  if (d.kind === 'resize' || d.kind === 'endpoint') return objects.map((o) => (o.id === d.id ? d.live || o : o));
  return objects;
}

function StylePanel({ o, onChange, textRef }) {
  const isText = o.type === 'text';
  const isFillable = o.type === 'rect' || o.type === 'ellipse' || (o.type === 'path' && o.closed);
  const range = 'w-24 accent-slate-900';
  return (
    <div data-testid="wb-style-panel" className="flex flex-col gap-3">
      {o.type === 'image' && (
        <p data-testid="wb-image-layer-note" className="text-[11px] text-sky-900 bg-sky-50 border border-sky-200 rounded px-2 py-1">
          Picture layer{o.name === 'AI illustration' ? ' (AI illustration)' : ''}: move, resize, rotate, fade, reorder or delete it. Its pixels are not editable shapes.
        </p>
      )}
      {isText && (
        <textarea ref={textRef} data-testid="wb-text-input" rows={2} value={o.text} onChange={(e) => onChange({ text: e.target.value })}
          className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" placeholder="Label text" />
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {isText && (
          <>
            <Field label="Color"><input type="color" data-testid="wb-text-color" value={o.color || '#0F172A'} onChange={(e) => onChange({ color: e.target.value })} className="h-7 w-9 rounded border border-slate-200" /></Field>
            <Field label={`Size ${o.fontSize || 24}`}><input type="range" min="10" max="96" data-testid="wb-font-size" value={o.fontSize || 24} onChange={(e) => onChange({ fontSize: Number(e.target.value) })} className={range} /></Field>
            <Field label="Bold"><input type="checkbox" data-testid="wb-bold" checked={!!o.bold} onChange={(e) => onChange({ bold: e.target.checked })} /></Field>
          </>
        )}
        {isFillable && (
          <Field label="Fill">
            <input type="color" data-testid="wb-fill-color" value={o.fill && o.fill !== 'none' ? o.fill : '#FFFFFF'} onChange={(e) => onChange({ fill: e.target.value })} className="h-7 w-9 rounded border border-slate-200" />
            <span className="normal-case tracking-normal flex items-center gap-1"><input type="checkbox" data-testid="wb-no-fill" checked={!o.fill || o.fill === 'none'} onChange={(e) => onChange({ fill: e.target.checked ? 'none' : '#E2E8F0' })} />none</span>
          </Field>
        )}
        {!isText && o.type !== 'image' && (
          <>
            <Field label="Stroke"><input type="color" data-testid="wb-stroke-color" value={o.stroke || '#334155'} onChange={(e) => onChange({ stroke: e.target.value })} className="h-7 w-9 rounded border border-slate-200" /></Field>
            <Field label={`Width ${o.strokeWidth ?? 2}`}><input type="range" min="0" max="20" step="0.5" data-testid="wb-stroke-width" value={o.strokeWidth ?? 2} onChange={(e) => onChange({ strokeWidth: Number(e.target.value) })} className={range} /></Field>
          </>
        )}
        <Field label={`Opacity ${Math.round((o.opacity ?? 1) * 100)}%`}><input type="range" min="0.05" max="1" step="0.05" data-testid="wb-opacity" value={o.opacity ?? 1} onChange={(e) => onChange({ opacity: Number(e.target.value) })} className={range} /></Field>
      </div>
    </div>
  );
}

function SelectionDetails({ objects, single, onSelect, onUnlock, onChange, textRef }) {
  if (!single) return null;
  if (single.locked) {
    return (
      <p data-testid="wb-locked-note" className="flex items-center gap-2 text-xs text-amber-700">
        <Lock className="h-3.5 w-3.5" />This object is locked.
        <button type="button" className="underline" onClick={onUnlock} data-testid="wb-unlock-inline">Unlock to edit</button>
      </p>
    );
  }
  return (
    <>
      {single.groupId && (
        <p data-testid="wb-in-group-note" className="text-xs text-slate-500">
          Part of {groupName(objects, single.groupId)}.{' '}
          <button type="button" className="underline" data-testid="wb-select-group"
            onClick={() => onSelect(groupMembers(objects, single.groupId).map((m) => m.id))}>Select whole group</button>
        </p>
      )}
      <StylePanel o={single} textRef={textRef} onChange={onChange} />
    </>
  );
}

export function WhiteboardEditor({ block, onUpdate }) {
  const c = { ...DEFAULT_WHITEBOARD, ...(block.content || {}) };
  const objects = Array.isArray(c.objects) ? c.objects : [];
  const svgRef = useRef(null);
  const textRef = useRef(null);
  const editorRef = useRef(null);
  const lastTap = useRef({ id: null, t: 0 });
  const [tool, setTool] = useState('select');
  const [selectedIds, setSelectedIds] = useState([]);
  const [multi, setMulti] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [artOpen, setArtOpen] = useState(false);
  const [full, setFull] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const live = applyDraft(objects, draft);
  const selObjs = live.filter((o) => selectedIds.includes(o.id));
  const single = selObjs.length === 1 ? selObjs[0] : null;
  const scale = svgRef.current ? WB_W / Math.max(1, svgRef.current.clientWidth) : 1;

  const apply = (next, patch = {}) => onUpdate({ ...block, content: { ...c, ...patch, objects: next } });
  const hist = useWhiteboardHistory(objects, apply);
  const commit = hist.commit;
  const updateObj = (id, p) => {
    if (objects.find((o) => o.id === id)?.locked) return;
    commit(objects.map((o) => (o.id === id ? { ...o, ...p } : o)), `edit:${id}:${Object.keys(p).join(',')}`);
  };
  const toSvg = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const r = pt.matrixTransform(svg.getScreenCTM().inverse());
    return clampPt({ x: r.x, y: r.y });
  };
  const capture = (e) => { try { svgRef.current.setPointerCapture(e.pointerId); } catch (_) { /* noop */ } };
  const focusText = () => setTimeout(() => textRef.current?.focus(), 30);
  // Pointer capture suppresses native focus + click/dblclick, so focus the editor (keyboard shortcuts) explicitly.
  const focusEditor = () => editorRef.current?.focus({ preventScroll: true });

  const startCreate = (e) => {
    const p = toSvg(e);
    capture(e);
    if (tool === 'text') {
      const o = { id: uid(), type: 'text', x: p.x, y: p.y, ...SHAPE_DEFAULTS.text };
      commit([...objects, o]); setSelectedIds([o.id]); setTool('select'); focusText();
      return;
    }
    const d = SHAPE_DEFAULTS[tool];
    let obj;
    if (tool === 'rect' || tool === 'ellipse') obj = { id: uid(), type: tool, x: p.x, y: p.y, w: 0, h: 0, ...d };
    else if (tool === 'line' || tool === 'arrow') obj = { id: uid(), type: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y, ...d };
    else obj = { id: uid(), type: 'path', points: [[p.x, p.y]], ...d };
    setDraft({ kind: 'create', tool, start: p, obj });
  };

  const onBackgroundDown = (e) => {
    focusEditor();
    if (tool === 'select') { if (!multi) setSelectedIds([]); return; }
    startCreate(e);
  };

  const onObjectDown = (e, o) => {
    if (tool !== 'select') return; // let creation tools bubble to the canvas
    e.stopPropagation();
    e.preventDefault(); // stop the compat mousedown from moving focus off the editor
    focusEditor();
    const now = Date.now(), isDouble = lastTap.current.id === o.id && now - lastTap.current.t < 400;
    lastTap.current = { id: o.id, t: isDouble ? 0 : now };
    if (isDouble) { onObjectDoubleClick(o); return; }
    if (multi || e.shiftKey || e.metaKey || e.ctrlKey) { setSelectedIds(toggleUnit(objects, selectedIds, o)); return; }
    const ids = clickSelection(objects, selectedIds, o);
    setSelectedIds(ids);
    const movable = objects.filter((x) => ids.includes(x.id));
    if (!movable.length || movable.some((x) => x.locked)) return; // any locked member pins the whole selection
    capture(e);
    setDraft({ kind: 'move', start: toSvg(e), origs: Object.fromEntries(movable.map((m) => [m.id, m])), live: {} });
  };

  const onObjectDoubleClick = (o) => {
    setSelectedIds([o.id]);
    if (o.type === 'text' && !o.locked) focusText();
  };

  const onHandleDown = (e, handle) => {
    e.stopPropagation();
    e.preventDefault();
    focusEditor();
    capture(e);
    if (handle === 'rotate') {
      const movable = objects.filter((x) => selectedIds.includes(x.id));
      if (!movable.length || movable.some((x) => x.locked)) return;
      const c = movable.length === 1 ? centerOf(movable[0]) : (() => { const u = unionBox(movable); return { x: u.x + u.w / 2, y: u.y + u.h / 2 }; })();
      setDraft({ kind: 'rotate', c, a0: angleAt(c, toSvg(e)), origs: Object.fromEntries(movable.map((m) => [m.id, m])), live: {} });
      return;
    }
    const o = single && objects.find((x) => x.id === single.id);
    if (!o || o.locked) return;
    if (handle === 'p1' || handle === 'p2') setDraft({ kind: 'endpoint', id: o.id, handle, orig: o, live: o });
    else setDraft({ kind: 'resize', id: o.id, handle, orig: o, live: o });
  };

  const onMove = (e) => {
    if (!draft) return;
    const p = toSvg(e);
    if (draft.kind === 'move') {
      const dx = p.x - draft.start.x, dy = p.y - draft.start.y;
      setDraft({ ...draft, live: Object.fromEntries(Object.values(draft.origs).map((o) => [o.id, moveObj(o, dx, dy)])) });
    } else if (draft.kind === 'rotate') {
      let deg = angleAt(draft.c, p) - draft.a0;
      if (e.shiftKey) deg = Math.round(deg / 15) * 15; // Shift snaps to 15° steps
      setDraft({ ...draft, live: Object.fromEntries(Object.values(draft.origs).map((o) => [o.id, rotateObj(o, deg, draft.c.x, draft.c.y)])) });
    } else if (draft.kind === 'endpoint') {
      const k = draft.handle === 'p1' ? { x1: p.x, y1: p.y } : { x2: p.x, y2: p.y };
      setDraft({ ...draft, live: { ...draft.orig, ...k } });
    } else if (draft.kind === 'resize') {
      setDraft({ ...draft, live: resizeToPoint(draft.orig, draft.handle, p) });
    } else if (draft.kind === 'create') {
      const s = draft.start, o = draft.obj;
      let obj;
      if (o.type === 'rect' || o.type === 'ellipse') obj = { ...o, x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) };
      else if (o.type === 'path') {
        const last = o.points[o.points.length - 1];
        obj = Math.hypot(p.x - last[0], p.y - last[1]) < 2 * scale ? o : { ...o, points: [...o.points, [p.x, p.y]] };
      } else obj = { ...o, x2: p.x, y2: p.y };
      setDraft({ ...draft, obj });
    }
  };

  // A whole drag / resize gesture commits once -> exactly one undo entry.
  const onUp = () => {
    if (!draft) return;
    const d = draft;
    setDraft(null);
    if (d.kind === 'create') {
      let o = d.obj;
      if ((o.type === 'rect' || o.type === 'ellipse') && o.w < 6 && o.h < 6) o = { ...o, w: 180, h: 110 };
      if ((o.type === 'line' || o.type === 'arrow') && Math.hypot(o.x2 - o.x1, o.y2 - o.y1) < 6) o = { ...o, x2: Math.min(WB_W, o.x1 + 180) };
      if (o.type === 'path' && o.points.length < 2) return;
      commit([...objects, o]); setSelectedIds([o.id]); setTool('select');
      return;
    }
    if (d.kind === 'move' || d.kind === 'rotate') {
      const moved = Object.values(d.live).some((n) => JSON.stringify(n) !== JSON.stringify(d.origs[n.id]));
      if (moved) { commit(objects.map((o) => d.live[o.id] || o)); lastTap.current = { id: null, t: 0 }; }
      return;
    }
    if (d.live && d.live !== d.orig) commit(objects.map((o) => (o.id === d.id ? d.live : o)));
  };

  const ids = selObjs.map((o) => o.id);
  const removeSelected = () => {
    const del = selObjs.filter((o) => !o.locked).map((o) => o.id);
    if (!del.length) return;
    commit(objects.filter((o) => !del.includes(o.id))); setSelectedIds(ids.filter((id) => !del.includes(id))); focusEditor();
  };
  const duplicate = () => { if (!ids.length) return; const r = duplicateSelection(objects, ids); commit(r.objects); setSelectedIds(r.ids); };
  const group = () => { if (ids.length > 1) commit(groupSelection(objects, ids)); };
  const ungroup = () => { if (selObjs.some((o) => o.groupId)) commit(ungroupSelection(objects, ids)); };
  const flag = (fids, patch) => commit(patchMany(objects, fids, patch));
  const rotateBy = (deg) => {
    if (!selObjs.length || selObjs.some((o) => o.locked)) return;
    const u = unionBox(selObjs), c = selObjs.length === 1 ? centerOf(selObjs[0]) : { x: u.x + u.w / 2, y: u.y + u.h / 2 };
    const next = Object.fromEntries(selObjs.map((o) => [o.id, rotateObj(o, deg, c.x, c.y)]));
    commit(objects.map((o) => next[o.id] || o));
  };
  const imageRef = useRef(null);
  // AI illustration layer: behind everything, or on top; never replaces existing content.
  const addLayer = ({ behind, ...layer }) => {
    commit(behind ? [layer, ...objects] : [...objects, layer]); setSelectedIds([layer.id]); setTool('select');
  };
  const [uploading, setUploading] = useState(false);
  const addImage = async (file) => {
    setUploading(true);
    try {
      const o = await uploadImageLayer(file);
      commit([...objects, o]); setSelectedIds([o.id]); setTool('select');
    } catch (e) { toast.error(e.message); } finally { setUploading(false); }
  };
  const groupIds = new Set(selObjs.map((o) => o.groupId || `solo:${o.id}`));
  const loadIceberg = () => {
    if (objects.length && !window.confirm('Replace everything on this whiteboard with the iceberg example?')) return;
    commit(icebergStarter()); setSelectedIds([]); setTool('select');
  };
  const onKeyDown = (e) => {
    if (/INPUT|TEXTAREA|SELECT/.test(e.target.tagName) || e.target.isContentEditable) return; // keep native text undo
    const mod = e.metaKey || e.ctrlKey, k = e.key.toLowerCase();
    if (mod && k === 'z') { e.preventDefault(); if (e.shiftKey) hist.redo(); else hist.undo(); return; }
    if (mod && k === 'y') { e.preventDefault(); hist.redo(); return; }
    if (mod && k === 'g') { e.preventDefault(); if (e.shiftKey) ungroup(); else group(); return; }
    if ((e.key === 'Delete' || e.key === 'Backspace') && ids.length) { e.preventDefault(); removeSelected(); }
    if (e.key === 'Escape') { setSelectedIds([]); setTool('select'); }
  };

  const visibleSel = selObjs.filter((o) => !o.hidden);
  const showSel = tool === 'select' && !(draft && draft.kind === 'create') && visibleSel.length > 0;
  const sb = showSel ? unionBox(visibleSel) : null;
  const handleObj = single && !single.locked && !single.hidden ? single : null;

  return (
    <div ref={editorRef} data-testid="whiteboard-editor" className="space-y-3 p-3 sm:p-4 bg-slate-50/60 rounded-lg border border-slate-200 outline-none" tabIndex={0} onKeyDown={onKeyDown}>
      <div className="flex flex-col sm:flex-row gap-2">
        <input className="flex-1 px-2 py-1.5 border border-slate-200 rounded text-sm" data-testid="wb-title-input" value={c.title}
          onChange={(e) => apply(objects, { title: e.target.value })} placeholder="Whiteboard title (optional)" />
        <Field label="Background"><input type="color" data-testid="wb-background" value={c.background || '#FFFFFF'} onChange={(e) => apply(objects, { background: e.target.value })} className="h-8 w-10 rounded border border-slate-200" /></Field>
      </div>
      <div className="flex flex-wrap gap-1.5" role="toolbar" aria-label="Whiteboard tools">
        <Btn title="Undo (Ctrl/Cmd+Z)" onClick={hist.undo} disabled={!hist.canUndo} testid="wb-undo"><Undo2 className="h-4 w-4" /><span className="hidden sm:inline">Undo</span></Btn>
        <Btn title="Redo (Ctrl/Cmd+Shift+Z)" onClick={hist.redo} disabled={!hist.canRedo} testid="wb-redo"><Redo2 className="h-4 w-4" /><span className="hidden sm:inline">Redo</span></Btn>
        <span className="w-px bg-slate-200 mx-1" />
        {TOOLS.map(({ id, label, Icon }) => (
          <Btn key={id} title={label} active={tool === id} onClick={() => setTool(id)} testid={`wb-tool-${id}`}><Icon className="h-4 w-4" /><span className="hidden sm:inline">{label}</span></Btn>
        ))}
        <Btn title="Image: add a picture as a movable, resizable, rotatable layer" onClick={() => imageRef.current?.click()} disabled={uploading} testid="wb-tool-image">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}<span className="hidden sm:inline">Image</span>
        </Btn>
        <input ref={imageRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" data-testid="wb-image-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) addImage(f); }} />
        <Btn title="Multi-select: tap objects to add or remove them" active={multi} onClick={() => { setMulti(!multi); setTool('select'); }} testid="wb-multi"><BoxSelect className="h-4 w-4" /><span className="hidden sm:inline">Multi-select</span></Btn>
        <Btn title="Layers" active={layersOpen} onClick={() => setLayersOpen(!layersOpen)} testid="wb-layers-toggle"><Layers className="h-4 w-4" /><span className="hidden sm:inline">Layers</span></Btn>
        <Btn title="Full screen view (read-only)" onClick={() => { setDraft(null); setFull(true); }} testid="wb-fullscreen-open"><Expand className="h-4 w-4" /><span className="hidden sm:inline">Full screen</span></Btn>
        <span className="w-px bg-slate-200 mx-1" />
        <Btn title="Iceberg example" onClick={loadIceberg} testid="wb-iceberg"><Mountain className="h-4 w-4" /><span className="hidden sm:inline">Iceberg example</span></Btn>
        <Btn title="Generate with AI" onClick={() => setAiOpen(true)} testid="wb-ai-open"><Sparkles className="h-4 w-4" /><span className="hidden sm:inline">Generate with AI</span></Btn>
        <Btn title="AI Illustration: generate a picture layer" onClick={() => setArtOpen(true)} testid="wb-art-open"><Wand2 className="h-4 w-4" /><span className="hidden sm:inline">AI Illustration</span></Btn>
        <Btn title="Picture to Perfect: rebuild a photo as an editable diagram" onClick={() => setPhotoOpen(true)} testid="wb-photo-open"><ImagePlus className="h-4 w-4" /><span className="hidden sm:inline">Picture to Perfect</span></Btn>
      </div>
      <AiWhiteboardDialog open={aiOpen} onOpenChange={setAiOpen} hasObjects={objects.length > 0} background={c.background}
        onAccept={(objs) => { commit(objs); setSelectedIds([]); setTool('select'); }} onAddLayer={addLayer} />
      <AiWhiteboardDialog mode="illustration" open={artOpen} onOpenChange={setArtOpen} hasObjects={objects.length > 0} background={c.background}
        onAccept={(objs) => { commit(objs); setSelectedIds([]); setTool('select'); }} onAddLayer={addLayer} />
      {full && <WhiteboardFullscreen content={{ ...c, objects }} onClose={() => { setFull(false); focusEditor(); }} />}
      <PictureToPerfectDialog open={photoOpen} onOpenChange={setPhotoOpen} hasObjects={objects.length > 0} background={c.background}
        onAccept={(objs) => { commit(objs); setSelectedIds([]); setTool('select'); }} />
      <div className="space-y-3">
        <div className="relative rounded-xl border border-slate-200 overflow-hidden shadow-sm" style={{ backgroundColor: c.background || '#FFFFFF' }}>
          <svg ref={svgRef} data-testid="wb-canvas" viewBox={`0 0 ${WB_W} ${WB_H}`} className="w-full h-auto block select-none"
            style={{ touchAction: 'none', cursor: tool === 'select' ? 'default' : 'crosshair' }}
            onPointerDown={onBackgroundDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
            <WhiteboardShapes objects={live} onObjectPointerDown={onObjectDown} />
            <SelectionLayer sb={sb} o={handleObj} multi={visibleSel.length > 1} locked={selObjs.some((o) => o.locked)} scale={scale}
              onHandleDown={onHandleDown} onBodyDown={(e) => handleObj && onObjectDown(e, handleObj)} />
          </svg>
          {objects.length === 0 && !draft && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none text-center px-6">
              <p className="text-sm text-slate-400">Pick a tool above and draw, or start from an example</p>
              <button type="button" onClick={loadIceberg} data-testid="wb-empty-iceberg" className="pointer-events-auto text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700">Start with the iceberg example</button>
            </div>
          )}
        </div>
        {layersOpen && <LayersPanel objects={objects} selectedIds={ids} onSelect={(s) => { setSelectedIds(s); setTool('select'); }} onFlag={flag} />}
      </div>
      {selObjs.length ? (
        <div className="flex flex-col gap-3 p-3 bg-white rounded-lg border border-slate-200">
          <ArrangeBar count={selObjs.length} canGroup={selObjs.length > 1 && groupIds.size > 1} canUngroup={selObjs.some((o) => o.groupId)}
            allLocked={selObjs.every((o) => o.locked)} allHidden={selObjs.every((o) => o.hidden)}
            onArrange={(dir) => commit(moveSelection(objects, ids, dir))} onGroup={group} onUngroup={ungroup}
            onLock={() => flag(ids, { locked: !selObjs.every((o) => o.locked) })} onHide={() => flag(ids, { hidden: !selObjs.every((o) => o.hidden) })}
            onDuplicate={duplicate} onDelete={removeSelected} />
          {selObjs.every((o) => !o.locked) ? (
            <GeometryPanel single={single} onRotateBy={rotateBy}
              onReplace={(n) => commit(objects.map((o) => (o.id === n.id ? n : o)), `geo:${n.id}`)} />
          ) : null}
          <SelectionDetails objects={objects} single={single} textRef={textRef} onSelect={setSelectedIds}
            onUnlock={() => flag([single.id], { locked: false })} onChange={(p) => updateObj(single.id, p)} />
        </div>
      ) : (
        <p className="text-[11px] text-slate-400">Tip: tap an object to select it (grouped cards select together; double-tap or use Layers to pick one part). Multi-select or Shift-click to choose several, then Group. Ctrl/Cmd+Z to undo.</p>
      )}
    </div>
  );
}

registerBlock({
  type: 'whiteboard',
  getDefaultContent: () => ({ ...DEFAULT_WHITEBOARD, objects: [] }),
  Editor: WhiteboardEditor,
  View: WhiteboardView,
});
